/*
 * File: About.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 16 May 2022 11:11:11 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */
import { BackTop, Button, Col, Layout, Row, Spin, Table } from 'antd';
import { AntPageTitle } from 'components/UI/AntPageTitle';
import { AtatWalker } from 'components/atoms/AtatWalker';
import { InlineStylesModel } from 'models/InlineStylesModel';
import * as React from 'react';
import { isMobileOnly } from 'react-device-detect';
import { Link, useNavigate } from 'react-router-dom';
import { useGetApiNameQuery as useBeautifulDayMonsterName, useGetVersionQuery as useBeautifulDayMonsterVersion } from 'redux/services/abbyCadabby/abbyCadabbyApi';
import { useGetApiNameQuery as useCookieMonsterName, useGetVersionQuery as useCookieMonsterVersion } from 'redux/services/cookieMonster/cookieMonsterApi';
import { useGetApiNameQuery as useMrsGrouch, useGetVersionQuery as useMrsGrouchVersion } from 'redux/services/mrsGrouch/mrsGrouchApi';
import { useAppSelector } from 'redux/store';
import { globalStyles } from '../../common/GlobalStyles';
import '../../common/styles/checkmarkStyle.less';
const styles: InlineStylesModel = {
  headerTitle: {
    color: 'rgb(67, 67, 73)',
    font: isMobileOnly ? '500 0.75rem Poppins' : '500 1rem Poppins',
    fontSize: 16
  },
  statusContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: isMobileOnly ? 'center' : 'none'
  },
  statusIcon: {
    fontSize: '1.5rem',
    marginRight: isMobileOnly ? '0px' : '15px'
  },
  statusText: {
    display: isMobileOnly ? 'none' : 'block'
  },
  tableBody: {
    backgroundColor: '#fff',
    fontSize: isMobileOnly ? '0.7em' : '1em',
    borderTop: '2px solid black',
    width: '100%'
  }
};

enum ApiStatus {
  online = 'Online',
  offline = 'Offline'
}

const columnHeaders = [
  {
    dataIndex: 'apiName',
    key: 'apiName',
    title: 'API Name'
  },
  {
    dataIndex: 'version',
    key: 'version',
    title: 'Version'
  },
  {
    dataIndex: 'status',
    key: 'status',
    title: 'Status'
  }
];

export const About: React.FunctionComponent = () => {
  /* ******************** Hooks ******************** */
  const navigate = useNavigate();

  const { acuityContext } = useAppSelector((state) => state.app);

  const { data: beautifulDayMonsterApiVersion, isLoading: isLoadingBeautifulDayMonster } = useBeautifulDayMonsterVersion();
  const { data: beautifulDayMonsterApiName, isLoading: isLoadingBeautifulDayMonsterName } = useBeautifulDayMonsterName();

  const { data: cookieMonsterApiVersion, isLoading: isLoadingCookieMonster } = useCookieMonsterVersion();
  const { data: cookieMonsterApiName, isLoading: isLoadingCookieMonsterName } = useCookieMonsterName();

  const { data: mrsGrouchApiVersion, isLoading: isLoadingMrsGrouch } = useMrsGrouchVersion();
  const { data: mrsGrouchApiName, isLoading: isLoadingmrsGrouchName } = useMrsGrouch();

  const handleHomePage = (): void => {
    if (acuityContext) navigate(`/${acuityContext?.selectedCustomer.id}/projects`);
    else navigate('/internal');
  };

  /* ******************** Table Data ******************** */

  const beautifulDayMonsterTableData = {
    version: beautifulDayMonsterApiVersion || 'failed',
    key: beautifulDayMonsterApiName,
    status: !isLoadingBeautifulDayMonster ? (
      <Row className="status-container" style={styles.statusContainer}>
        <Col style={styles.statusIcon}>
          {beautifulDayMonsterApiVersion ? (
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130.2 130.2">
              <circle className="path circle" fill="none" stroke="#73AF55" strokeWidth="10" strokeMiterlimit="10" cx="65.1" cy="65.1" r="62.1" />
              <polyline className="path check" fill="none" stroke="#73AF55" strokeWidth="10" strokeLinecap="round" strokeMiterlimit="10" points="100.2,40.2 51.5,88.8 29.8,67.5 " />
            </svg>
          ) : (
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130.2 130.2">
              <circle className="path circle" fill="none" stroke="#D06079" strokeWidth="7" strokeMiterlimit="10" cx="65.1" cy="65.1" r="62.1" />
              <line className="path line" fill="none" stroke="#D06079" strokeWidth="7" strokeLinecap="round" strokeMiterlimit="10" x1="34.4" y1="37.9" x2="95.8" y2="92.3" />
              <line className="path line" fill="none" stroke="#D06079" strokeWidth="7" strokeLinecap="round" strokeMiterlimit="10" x1="95.8" y1="38" x2="34.4" y2="92.2" />
            </svg>
          )}
        </Col>
        <Col style={styles.statusText}>{beautifulDayMonsterApiVersion ? ApiStatus.online : ApiStatus.offline}</Col>
      </Row>
    ) : (
      <Row>
        <Col style={styles.statusIcon}>
          <Spin spinning />
        </Col>
      </Row>
    ),
    apiName: !isLoadingBeautifulDayMonsterName ? (
      <Row>
        <Col>{beautifulDayMonsterApiName || 'beautiful-day-monster'}</Col>
      </Row>
    ) : (
      <Row>
        <Col style={styles.statusIcon}>
          <Spin spinning />
        </Col>
      </Row>
    )
  };

  const cookieMonsterTableData = {
    version: cookieMonsterApiVersion || 'failed',
    key: cookieMonsterApiName,
    status: !isLoadingCookieMonster ? (
      <Row className="status-container" style={styles.statusContainer}>
        <Col style={styles.statusIcon}>
          {cookieMonsterApiVersion ? (
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130.2 130.2">
              <circle className="path circle" fill="none" stroke="#73AF55" strokeWidth="10" strokeMiterlimit="10" cx="65.1" cy="65.1" r="62.1" />
              <polyline className="path check" fill="none" stroke="#73AF55" strokeWidth="10" strokeLinecap="round" strokeMiterlimit="10" points="100.2,40.2 51.5,88.8 29.8,67.5 " />
            </svg>
          ) : (
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130.2 130.2">
              <circle className="path circle" fill="none" stroke="#D06079" strokeWidth="7" strokeMiterlimit="10" cx="65.1" cy="65.1" r="62.1" />
              <line className="path line" fill="none" stroke="#D06079" strokeWidth="7" strokeLinecap="round" strokeMiterlimit="10" x1="34.4" y1="37.9" x2="95.8" y2="92.3" />
              <line className="path line" fill="none" stroke="#D06079" strokeWidth="7" strokeLinecap="round" strokeMiterlimit="10" x1="95.8" y1="38" x2="34.4" y2="92.2" />
            </svg>
          )}
        </Col>
        <Col style={styles.statusText}>{cookieMonsterApiVersion ? ApiStatus.online : ApiStatus.offline}</Col>
      </Row>
    ) : (
      <Row>
        <Col style={styles.statusIcon}>
          <Spin spinning />
        </Col>
      </Row>
    ),
    apiName: !isLoadingCookieMonsterName ? (
      <Row>
        <Col>{cookieMonsterApiName || 'Problem Getting Cookie Monster Name..'}</Col>
      </Row>
    ) : (
      <Row>
        <Col style={styles.statusIcon}>
          <Spin spinning />
        </Col>
      </Row>
    )
  };

  const mrsGrouchTableData = {
    version: mrsGrouchApiVersion || 'failed',
    key: mrsGrouchApiName,
    status: !isLoadingMrsGrouch ? (
      <Row className="status-container" style={styles.statusContainer}>
        <Col style={styles.statusIcon}>
          {mrsGrouchApiVersion ? (
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130.2 130.2">
              <circle className="path circle" fill="none" stroke="#73AF55" strokeWidth="10" strokeMiterlimit="10" cx="65.1" cy="65.1" r="62.1" />
              <polyline className="path check" fill="none" stroke="#73AF55" strokeWidth="10" strokeLinecap="round" strokeMiterlimit="10" points="100.2,40.2 51.5,88.8 29.8,67.5 " />
            </svg>
          ) : (
            <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130.2 130.2">
              <circle className="path circle" fill="none" stroke="#D06079" strokeWidth="7" strokeMiterlimit="10" cx="65.1" cy="65.1" r="62.1" />
              <line className="path line" fill="none" stroke="#D06079" strokeWidth="7" strokeLinecap="round" strokeMiterlimit="10" x1="34.4" y1="37.9" x2="95.8" y2="92.3" />
              <line className="path line" fill="none" stroke="#D06079" strokeWidth="7" strokeLinecap="round" strokeMiterlimit="10" x1="95.8" y1="38" x2="34.4" y2="92.2" />
            </svg>
          )}
        </Col>
        <Col style={styles.statusText}>{mrsGrouchApiVersion ? ApiStatus.online : ApiStatus.offline}</Col>
      </Row>
    ) : (
      <Row>
        <Col style={styles.statusIcon}>
          <Spin spinning />
        </Col>
      </Row>
    ),
    apiName: !isLoadingmrsGrouchName ? (
      <Row>
        <Col>{mrsGrouchApiName || 'Problem Getting Mrs Grouch Name..'}</Col>
      </Row>
    ) : (
      <Row>
        <Col style={styles.statusIcon}>
          <Spin spinning />
        </Col>
      </Row>
    )
  };

  const mainAppVersion = (): string => {
    const appName = process.env.REACT_APP_APPLICATION_NAME as string;
    const versionNumber = process.env.REACT_APP_VERSION_NUMBER as string;
    const splitVersion = versionNumber.split('-');

    return `${appName} ${splitVersion[splitVersion.length - 1]}`;
  };

  return (
    <Layout.Content style={{ ...globalStyles.addressGridContainer }}>
      <BackTop />
      <Row gutter={[10, 10]} justify="space-between">
        <Col span={24}>
          <Row justify="space-between" align="middle">
            <Col>
              <AntPageTitle text="About Website" />
              <Link to={`/release-notes/${process.env.REACT_APP_VERSION_NUMBER?.split('-')[0]}`} style={styles.headerTitle}>
                {mainAppVersion()}
              </Link>
            </Col>
            <Col>
              <Button onClick={handleHomePage} type="primary">
                Back to Home
              </Button>
            </Col>
          </Row>
        </Col>
        <Col md={13} sm={24}>
          <Table columns={columnHeaders} bordered={true} pagination={false} dataSource={[beautifulDayMonsterTableData, mrsGrouchTableData, cookieMonsterTableData]} style={styles.tableBody} />
        </Col>
        <Col md={10} sm={24}>
          <AtatWalker />
        </Col>
      </Row>
    </Layout.Content>
  );
};
